/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Card, CardContent, CardHeader } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Clock, Calendar, Settings, Timer, Globe, CheckCircle } from "lucide-react";

interface ScheduleBuilderFieldProps {
  field: any;
  name: string;
  control: Control<any>;
}

interface ScheduleConfig {
  mode: 'visual' | 'cron';
  frequency: 'daily' | 'weekly' | 'monthly' | 'hourly' | 'minutes';
  time: string;
  timezone: string;
  interval?: number; // Para "A cada X minutos"
  daysOfWeek?: number[];
  dayOfMonth?: number;
  customCron?: string;
}

// Timezone options mais comuns
const TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)', country: 'BR' },
  { value: 'America/New_York', label: 'Nova York (UTC-5)', country: 'US' },
  { value: 'Europe/London', label: 'Londres (UTC+0)', country: 'GB' },
  { value: 'Europe/Madrid', label: 'Madrid (UTC+1)', country: 'ES' },
  { value: 'Asia/Tokyo', label: 'Tóquio (UTC+9)', country: 'JP' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', country: '🌍' },
];

const ScheduleBuilderField: React.FC<ScheduleBuilderFieldProps> = ({
  field,
  name,
  control,
}) => {
  const { setValue, watch, register } = useFormContext();
  
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    mode: 'visual',
    frequency: 'daily',
    time: '09:00',
    timezone: 'America/Sao_Paulo',
    interval: 5, // Padrão para modo "A cada X minutos"
  });

  // Registrar os campos cron e timezone dinamicamente no form
  useEffect(() => {
    register('cron', { value: '' });
    register('timezone', { value: '' });
    console.log('[ScheduleBuilderField] Campos cron e timezone registrados no form');
  }, [register]);

  // Função para converter configuração visual para cron
  const convertToCron = (config: ScheduleConfig): { cron: string; timezone: string } => {
    const [hours, minutes] = config.time.split(':').map(Number);
    
    let cronExpression = '';
    
    switch (config.frequency) {
      case 'minutes': {
        // "A cada X minutos" - usar interval
        const interval = config.interval || 5;
        cronExpression = `*/${interval} * * * *`;
        break;
      }
      case 'hourly':
        // "A cada hora em minuto específico"
        cronExpression = `${minutes} * * * *`;
        break;
      case 'daily':
        cronExpression = `${minutes} ${hours} * * *`;
        break;
      case 'weekly': {
        const daysOfWeek = config.daysOfWeek || [1]; // Segunda-feira por padrão
        cronExpression = `${minutes} ${hours} * * ${daysOfWeek.join(',')}`;
        break;
      }
      case 'monthly': {
        const dayOfMonth = config.dayOfMonth || 1;
        cronExpression = `${minutes} ${hours} ${dayOfMonth} * *`;
        break;
      }
      default:
        cronExpression = `${minutes} ${hours} * * *`;
    }
    
    return {
      cron: cronExpression,
      timezone: config.timezone
    };
  };

  // Atualizar campos do form quando scheduleConfig muda (apenas se não há valores externos já carregados)
  useEffect(() => {
    // Verificar se já existem valores no form vindos do backend
    const existingCron = watch('cron');
    const existingTimezone = watch('timezone');
    
    console.log('[ScheduleBuilderField] 🔍 Verificando valores existentes no form:', {
      existingCron, 
      existingTimezone,
      scheduleConfigMode: scheduleConfig.mode
    });
    
    // Se há valores externos e não vieram do próprio ScheduleBuilderField, não sobrescrever
    if (existingCron && existingTimezone && scheduleConfig.mode === 'visual') {
      // Verificar se os valores existentes são diferentes dos valores padrão do ScheduleBuilderField
      const defaultCron = convertToCron(scheduleConfig).cron;
      
      // Se o valor existente for diferente do que seria gerado, significa que veio de fora
      if (existingCron !== defaultCron) {
        console.log('[ScheduleBuilderField] ⚠️ Valores externos detectados, não sobrescrevendo:', {
          existing: { cron: existingCron, timezone: existingTimezone },
          wouldGenerate: convertToCron(scheduleConfig),
          reason: 'Valor existente diferente do padrão gerado'
        });
        return;
      }
      
      // Se os valores são iguais, verificar se já foram definidos antes para evitar loops
      if (existingCron === defaultCron) {
        console.log('[ScheduleBuilderField] ⚠️ Valores já sincronizados, evitando loop:', {
          existing: { cron: existingCron, timezone: existingTimezone },
          generated: convertToCron(scheduleConfig)
        });
        return;
      }
    }
    
    if (scheduleConfig.mode === 'visual') {
      const { cron, timezone } = convertToCron(scheduleConfig);
      
      setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
      setValue('timezone', timezone, { shouldDirty: true, shouldTouch: true });
      
      console.log('[ScheduleBuilderField] ✅ Atualizando campos do form (visual):', { cron, timezone });
      console.log('[ScheduleBuilderField] ✅ Valores atuais no form:', { 
        cronValue: watch('cron'), 
        timezoneValue: watch('timezone') 
      });
    } else if (scheduleConfig.mode === 'cron' && scheduleConfig.customCron) {
      setValue('cron', scheduleConfig.customCron, { shouldDirty: true, shouldTouch: true });
      setValue('timezone', scheduleConfig.timezone, { shouldDirty: true, shouldTouch: true });
      
      console.log('[ScheduleBuilderField] ✅ Atualizando campos do form (cron):', { 
        cron: scheduleConfig.customCron, 
        timezone: scheduleConfig.timezone 
      });
      console.log('[ScheduleBuilderField] ✅ Valores atuais no form:', { 
        cronValue: watch('cron'), 
        timezoneValue: watch('timezone') 
      });
    }
  }, [scheduleConfig, setValue, watch]);

  // Função para converter cron de volta para configuração visual
  const convertFromCron = (cronExpression: string, timezone: string) => {
    if (!cronExpression) return null;
    
    console.log('[ScheduleBuilderField] 🔄 Convertendo cron para visual:', { cronExpression, timezone });
    
    // Parse do cron: "minuto hora dia mês dia_da_semana"
    const parts = cronExpression.trim().split(' ');
    if (parts.length !== 5) {
      console.log('[ScheduleBuilderField] ⚠️ Cron inválido, usando modo cron');
      return {
        mode: 'cron' as const,
        customCron: cronExpression,
        timezone: timezone || 'America/Sao_Paulo'
      };
    }
    
    const [minute, hour, day, , dayOfWeek] = parts;
    
    // Converter horário
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);
    const timeString = `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;
    
    // Detectar frequência
    if (dayOfWeek !== '*') {
      // Semanal - dias específicos da semana
      const days = dayOfWeek.split(',');
      return {
        mode: 'visual' as const,
        frequency: 'weekly' as const,
        time: timeString,
        daysOfWeek: days,
        timezone: timezone || 'America/Sao_Paulo'
      };
    } else if (day !== '*') {
      // Mensal - dia específico do mês
      return {
        mode: 'visual' as const,
        frequency: 'monthly' as const,
        time: timeString,
        dayOfMonth: parseInt(day),
        timezone: timezone || 'America/Sao_Paulo'
      };
    } else if (minute.startsWith('*/') && hour === '*') {
      // "A cada X minutos" - detectar padrão */N * * * *
      const intervalMatch = minute.match(/\*\/(\d+)/);
      const interval = intervalMatch ? parseInt(intervalMatch[1]) : 5;
      return {
        mode: 'visual' as const,
        frequency: 'minutes' as const,
        time: '00:00', // Não relevante para modo minutes
        interval: interval,
        timezone: timezone || 'America/Sao_Paulo'
      };
    } else if (hour === '*' && !minute.startsWith('*/')) {
      // "A cada hora em minuto específico" - detectar padrão N * * * *
      return {
        mode: 'visual' as const,
        frequency: 'hourly' as const,
        time: `00:${minuteNum.toString().padStart(2, '0')}`, // Formato para campo de minuto
        timezone: timezone || 'America/Sao_Paulo'
      };
    } else {
      // Diário - padrão N N * * *
      return {
        mode: 'visual' as const,
        frequency: 'daily' as const,
        time: timeString,
        timezone: timezone || 'America/Sao_Paulo'
      };
    }
  };

  // Sincronizar com valores existentes do form ao inicializar
  useEffect(() => {
    const currentCron = watch('cron');
    const currentTimezone = watch('timezone');
    
    if (currentCron || currentTimezone) {
      console.log('[ScheduleBuilderField] 🔄 Sincronizando com valores existentes:', { currentCron, currentTimezone });
      
      // Tentar converter o cron para configuração visual
      const parsedConfig = convertFromCron(currentCron, currentTimezone);
      
      if (parsedConfig) {
        console.log('[ScheduleBuilderField] ✅ Configuração visual recuperada:', parsedConfig);
        setScheduleConfig(prev => ({ ...prev, ...parsedConfig }) as ScheduleConfig);
      } else {
        // Fallback para valores atuais
        setScheduleConfig(prev => ({
          ...prev,
          timezone: (currentTimezone as string) || prev.timezone,
          mode: 'cron' as const,
          customCron: currentCron as string
        }));
      }
    } else {
      console.log('[ScheduleBuilderField] 🎯 Inicializando com valores padrão');
      // Inicializar com valores padrão
      const { cron, timezone } = convertToCron(scheduleConfig);
      setValue('cron', cron, { shouldDirty: true });
      setValue('timezone', timezone, { shouldDirty: true });
      console.log('[ScheduleBuilderField] 🎯 Valores padrão definidos:', { cron, timezone });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas na inicialização

  const getFrequencyDescription = () => {
    
    switch (scheduleConfig.frequency) {
      case 'hourly':
        return `A cada hora aos ${scheduleConfig.time.split(':')[1]} minutos`;
      case 'daily':
        return `Diariamente às ${scheduleConfig.time}`;
      case 'weekly': {
        const days = scheduleConfig.daysOfWeek || [1];
        const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
        const selectedDays = days.map(d => dayNames[d]).join(', ');
        return `Semanalmente ${selectedDays} às ${scheduleConfig.time}`;
      }
      case 'monthly':
        return `Mensalmente no dia ${scheduleConfig.dayOfMonth || 1} às ${scheduleConfig.time}`;
      default:
        return `Configuração personalizada`;
    }
  };

  const renderVisualBuilder = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Frequência</label>
          <Select
            value={scheduleConfig.frequency}
            onValueChange={(value: any) => {
              // Atualizar estado interno
              const newConfig = { ...scheduleConfig, frequency: value };
              setScheduleConfig(prev => ({ ...prev, frequency: value }));
              
              // Gerar nova expressão cron e sincronizar com o formulário
              const { cron } = convertToCron(newConfig);
              setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
              
              console.log('[ScheduleBuilderField] ⏰ Frequência alterada:', { 
                newFrequency: value,
                newCron: cron,
                formValue: watch('cron')
              });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minutes">
                <div className="flex items-center gap-2">
                  <Timer className="h-4 w-4" />
                  <span>A cada X minutos</span>
                </div>
              </SelectItem>
              <SelectItem value="hourly">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>A cada hora</span>
                </div>
              </SelectItem>
              <SelectItem value="daily">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Diariamente</span>
                </div>
              </SelectItem>
              <SelectItem value="weekly">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Semanalmente</span>
                </div>
              </SelectItem>
              <SelectItem value="monthly">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Mensalmente</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {scheduleConfig.frequency === 'minutes' ? 'Minutos' : 
             scheduleConfig.frequency === 'hourly' ? 'No minuto' : 'Horário'}
          </label>
          {scheduleConfig.frequency === 'minutes' ? (
            <Input
              type="number"
              min="1"
              max="59"
              value={scheduleConfig.interval || 5}
              onChange={(e) => {
                const interval = parseInt(e.target.value) || 5;
                const newConfig = { ...scheduleConfig, interval };
                setScheduleConfig(prev => ({ ...prev, interval }));
                
                // Gerar nova expressão cron e sincronizar com o formulário
                const { cron } = convertToCron(newConfig);
                setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
                
                console.log('[ScheduleBuilderField] ⚡ Intervalo alterado:', { 
                  interval,
                  newCron: cron,
                  formValue: watch('cron')
                });
              }}
              placeholder="5"
              className="w-full"
            />
          ) : scheduleConfig.frequency === 'hourly' ? (
            <Input
              type="number"
              min="0"
              max="59"
              value={scheduleConfig.time.split(':')[1]}
              onChange={(e) => {
                const newTime = `00:${e.target.value.padStart(2, '0')}`;
                const newConfig = { ...scheduleConfig, time: newTime };
                setScheduleConfig(prev => ({ 
                  ...prev, 
                  time: newTime
                }));
                
                // Gerar nova expressão cron e sincronizar com o formulário
                const { cron } = convertToCron(newConfig);
                setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
                
                console.log('[ScheduleBuilderField] 🕰 Minuto alterado (hourly):', { 
                  newTime,
                  newCron: cron,
                  formValue: watch('cron')
                });
              }}
              placeholder="30"
              className="w-full"
            />
          ) : (
            <Input
              type="time"
              value={scheduleConfig.time}
              onChange={(e) => {
                const newConfig = { ...scheduleConfig, time: e.target.value };
                setScheduleConfig(prev => ({ ...prev, time: e.target.value }));
                
                // Gerar nova expressão cron e sincronizar com o formulário
                const { cron } = convertToCron(newConfig);
                setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
                
                console.log('[ScheduleBuilderField] 🕰 Horário alterado:', { 
                  newTime: e.target.value,
                  newCron: cron,
                  formValue: watch('cron')
                });
              }}
              className="w-full"
            />
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Timezone
        </label>
        <Select
          value={scheduleConfig.timezone}
          onValueChange={(value) => {
            // Atualizar estado interno
            setScheduleConfig(prev => ({ ...prev, timezone: value }));
            
            // Sincronizar com o formulário imediatamente
            setValue('timezone', value, { shouldDirty: true, shouldTouch: true });
            
            console.log('[ScheduleBuilderField] 🌍 Timezone alterado (modo visual):', { 
              newTimezone: value,
              formValue: watch('timezone')
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONE_OPTIONS.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                <div className="flex items-center gap-2">
                  <span>{tz.country}</span>
                  <span>{tz.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {scheduleConfig.frequency === 'weekly' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Dias da Semana</label>
          <div className="flex flex-wrap gap-2">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, index) => (
              <Button
                key={index}
                type="button"
                variant={scheduleConfig.daysOfWeek?.includes(index) ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const currentDays = scheduleConfig.daysOfWeek || [];
                  const newDays = currentDays.includes(index)
                    ? currentDays.filter(d => d !== index)
                    : [...currentDays, index];
                  
                  const newConfig = { ...scheduleConfig, daysOfWeek: newDays };
                  setScheduleConfig(prev => ({ ...prev, daysOfWeek: newDays }));
                  
                  // Gerar nova expressão cron e sincronizar com o formulário
                  const { cron } = convertToCron(newConfig);
                  setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
                  
                  console.log('[ScheduleBuilderField] 🗺 Dias da semana alterados:', { 
                    newDays,
                    newCron: cron,
                    formValue: watch('cron')
                  });
                }}
                className="flex-1 min-w-0"
              >
                {day}
              </Button>
            ))}
          </div>
        </div>
      )}

      {scheduleConfig.frequency === 'monthly' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Dia do Mês</label>
          <Input
            type="number"
            min="1"
            max="31"
            value={scheduleConfig.dayOfMonth || 1}
            onChange={(e) => {
              const dayOfMonth = parseInt(e.target.value);
              const newConfig = { ...scheduleConfig, dayOfMonth };
              setScheduleConfig(prev => ({ ...prev, dayOfMonth }));
              
              // Gerar nova expressão cron e sincronizar com o formulário
              const { cron } = convertToCron(newConfig);
              setValue('cron', cron, { shouldDirty: true, shouldTouch: true });
              
              console.log('[ScheduleBuilderField] 🗺 Dia do mês alterado:', { 
                dayOfMonth,
                newCron: cron,
                formValue: watch('cron')
              });
            }}
            className="w-full"
          />
        </div>
      )}
      
      {/* Preview da configuração */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-foreground">Preview da Configuração</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setScheduleConfig(prev => ({ ...prev, mode: 'cron' }))}
            className="p-1.5 h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted/70 rounded transition-colors"
            title="Configuração Avançada"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          {getFrequencyDescription()}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="secondary" className="font-mono">
            {convertToCron(scheduleConfig).cron}
          </Badge>
          <span className="text-muted-foreground">
            {TIMEZONE_OPTIONS.find(tz => tz.value === scheduleConfig.timezone)?.label}
          </span>
        </div>
      </div>
    </div>
  );

  const renderCronBuilder = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Expressão Cron</label>
        <Input
          placeholder="0 9 * * * (diariamente às 9:00)"
          value={scheduleConfig.customCron || ''}
          onChange={(e) => {
            setScheduleConfig(prev => ({ ...prev, customCron: e.target.value }));
            // Sincronizar com o formulário
            setValue('cron', e.target.value, { shouldDirty: true, shouldTouch: true });
          }}
          className="font-mono"
        />
        <div className="text-xs text-muted-foreground">
          Formato: <code>minuto hora dia mês dia-da-semana</code>
        </div>
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Timezone
        </label>
        <Select
          value={scheduleConfig.timezone}
          onValueChange={(value) => {
            // Atualizar estado interno
            setScheduleConfig(prev => ({ ...prev, timezone: value }));
            
            // Sincronizar com o formulário imediatamente
            setValue('timezone', value, { shouldDirty: true, shouldTouch: true });
            
            console.log('[ScheduleBuilderField] 🌍 Timezone alterado (modo cron):', { 
              newTimezone: value,
              formValue: watch('timezone')
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONE_OPTIONS.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                <div className="flex items-center gap-2">
                  <span>{tz.country}</span>
                  <span>{tz.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Exemplos de expressões cron:</strong>
          <ul className="mt-2 space-y-1 text-xs">
            <li><code>0 9 * * *</code> - Diariamente às 9:00</li>
            <li><code>30 14 * * 1-5</code> - Seg-Sex às 14:30</li>
            <li><code>0 0 1 * *</code> - Primeiro dia de cada mês à meia-noite</li>
            <li><code>*/15 * * * *</code> - A cada 15 minutos</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: _formField }) => (
        <FormItem>
          <FormControl>
            <Card className="w-full">
              <CardHeader className="pb-4">
              </CardHeader>
              <CardContent className="space-y-4">
                {scheduleConfig.mode === 'visual' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      {field.description || "Configure quando o workflow deve ser executado de forma visual e intuitiva"}
                    </p>
                    {renderVisualBuilder()}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setScheduleConfig(prev => ({ ...prev, mode: 'visual' }))}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                      >
                        <Calendar className="h-4 w-4" />
                        ← Voltar para Configuração Visual
                      </Button>
                      <div className="flex-1" />
                    </div>
                    {renderCronBuilder()}
                  </div>
                )}
              </CardContent>
            </Card>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default ScheduleBuilderField;
